import type { Job, JobStatus } from '@jobos/shared';

export interface BarSegment {
  kind: 'paid' | 'billed' | 'tobill' | 'running';
  /** Width as a percentage of the track. Ignored when `fill` is set. */
  pct: number;
  /** The dollar amount, for the title and the accessible label. */
  raw: number;
  /** Render with flex:1 1 auto so flooring never overflows the track. */
  fill?: boolean;
}

/** Below this a segment is sub-pixel and effectively invisible. */
const MIN_PCT = 1.5;

export function billingBarSegments(
  summary: Job['jobBillingSummary'],
  jobStatus: JobStatus,
): BarSegment[] {
  // No denominator means no honest bar. Drawing one scaled to `invoiced`
  // would read as fully billed, which is the exact lie this avoids.
  if (!summary || summary.contractValue === null) return [];

  const { contractValue, invoiced, collected } = summary;
  const paid = Math.max(0, collected);
  const billed = Math.max(0, invoiced - collected);
  const unbilled = Math.max(0, contractValue - invoiced);

  // Overbilled jobs widen the denominator rather than overflowing the track.
  const total = Math.max(contractValue, invoiced);
  if (total <= 0) return [];

  const out: BarSegment[] = [];
  const push = (kind: BarSegment['kind'], raw: number) => {
    if (raw <= 0) return;
    out.push({ kind, raw, pct: Math.max((raw / total) * 100, MIN_PCT) });
  };

  push('paid', paid);
  push('billed', billed);
  push(jobStatus === 'completed' ? 'tobill' : 'running', unbilled);

  // MIN_PCT can inflate the sum past 100%, so one segment flexes to absorb the
  // difference. It must be the LARGEST: the filler's own pct is discarded at
  // render time, and a floored sliver chosen as filler would collapse back to
  // sub-pixel — defeating the floor exactly where it was needed.
  //
  // Iterate values, not indices: noUncheckedIndexedAccess makes out[i] a
  // `BarSegment | undefined` and the index form produces three TS2532s.
  if (out.length) {
    let largest = out[0]!;
    for (const seg of out) if (seg.raw > largest.raw) largest = seg;
    largest.fill = true;
  }
  return out;
}
